import type { InfiniteData } from '@tanstack/react-query'

import { splitThoughtFromMarkdown } from '@/features/shared/chat/conversation/history'
import type {
  RuntimeConversationDto,
  RuntimeConversationListDto,
  RuntimeMessageDto,
} from '@/features/shared/chat/types'
import { formatDateInTimezone, getCalendarDate } from '@/features/shared/timezone/timezone'

export const SIDEBAR_CONVERSATION_LIMIT = 5
export const HISTORY_PREVIEW_MAX_LENGTH = 132
export const HISTORY_GROUP_ORDER = ['today', 'thisMonth', 'thisYear', 'earlier', 'all'] as const

export type HistoryGroupKey = (typeof HISTORY_GROUP_ORDER)[number]

export function conversationLabel(conversation: RuntimeConversationDto, fallback: string) {
  const name = conversation.name?.trim()

  return name && name.length > 0 ? name : fallback
}

/**
 * 生成历史列表可读预览。
 *
 * runtime provider 返回的 answer 可能包含 thought、Markdown、代码块或 HTML 片段；
 * 侧边栏和历史页只需要短文本摘要，因此这里统一做降噪，避免每个页面各自猜字段。
 */
export function compactMarkdownPreview(markdown: string) {
  const preview = splitThoughtFromMarkdown(markdown)
    .content.replace(/```[a-zA-Z0-9_-]*\n?/g, ' ')
    .replace(/```/g, ' ')
    .replace(/\*\*/g, '')
    .replace(/(^|\s)#{1,6}\s*/g, ' ')
    .replace(/-{3,}/g, ' ')
    .replace(/(^|\s)[*-]\s+/g, ' ')
    .replace(/\|+/g, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return preview.length > HISTORY_PREVIEW_MAX_LENGTH
    ? `${preview.slice(0, HISTORY_PREVIEW_MAX_LENGTH).trimEnd()}…`
    : preview
}

export function getConversationPreview(conversation: RuntimeConversationDto) {
  const lastMessage =
    conversation.last_message_role === 'assistant' ? conversation.last_message : undefined
  const previewSource = [
    conversation.answer_preview,
    conversation.last_answer,
    conversation.last_assistant_message,
    conversation.summary,
    lastMessage,
  ].find((value) => typeof value === 'string' && value.trim().length > 0)

  if (!previewSource) {
    return ''
  }

  return compactMarkdownPreview(previewSource)
}

/**
 * 兼容 provider 历史 DTO 的时间格式差异。
 *
 * 后端和 runtime provider 当前可能返回秒级时间戳、毫秒级时间戳或 ISO 字符串；
 * 调用方只关心本地展示和分组，所以解析失败统一返回 null。
 */
export function parseConversationTimestamp(value: RuntimeConversationDto['updated_at']) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const milliseconds = value < 1_000_000_000_000 ? value * 1000 : value
    const date = new Date(milliseconds)

    return Number.isNaN(date.getTime()) ? null : date
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const date = new Date(value)

    return Number.isNaN(date.getTime()) ? null : date
  }

  return null
}

function sameCalendarDay(left: Date, right: Date, timeZone?: string) {
  const leftDate = getCalendarDate(left, timeZone)
  const rightDate = getCalendarDate(right, timeZone)

  return (
    leftDate.year === rightDate.year &&
    leftDate.month === rightDate.month &&
    leftDate.day === rightDate.day
  )
}

export function formatConversationTime(conversation: RuntimeConversationDto, timeZone?: string) {
  const date =
    parseConversationTimestamp(conversation.updated_at) ??
    parseConversationTimestamp(conversation.created_at)

  if (!date) {
    return ''
  }

  const now = new Date()

  if (sameCalendarDay(date, now, timeZone)) {
    return formatDateInTimezone(date, { hour: '2-digit', minute: '2-digit' }, timeZone)
  }

  return formatDateInTimezone(date, { day: '2-digit', month: '2-digit' }, timeZone)
}

function getRuntimeMessagePreview(message: RuntimeMessageDto) {
  const candidate =
    message.answer ?? (message.role === 'assistant' ? (message.content ?? message.text ?? '') : '')

  return compactMarkdownPreview(candidate)
}

export function getFirstAssistantReplyPreview(messages: RuntimeMessageDto[]) {
  return messages.map(getRuntimeMessagePreview).find((preview) => preview.length > 0) ?? ''
}

export function getHistoryGroupKey(
  conversation: RuntimeConversationDto,
  timeZone?: string,
): HistoryGroupKey {
  const date =
    parseConversationTimestamp(conversation.updated_at) ??
    parseConversationTimestamp(conversation.created_at)

  if (!date) {
    return 'all'
  }

  const now = new Date()
  const dateParts = getCalendarDate(date, timeZone)
  const nowParts = getCalendarDate(now, timeZone)

  if (sameCalendarDay(date, now, timeZone)) {
    return 'today'
  }

  if (dateParts.year === nowParts.year && dateParts.month === nowParts.month) {
    return 'thisMonth'
  }

  if (dateParts.year === nowParts.year) {
    return 'thisYear'
  }

  return 'earlier'
}

function getPinnedTimestamp(conversation: RuntimeConversationDto) {
  return parseConversationTimestamp(conversation.pinned_at)?.getTime() ?? 0
}

export function sortPinnedConversations(conversations: RuntimeConversationDto[]) {
  return conversations
    .map((conversation, index) => ({ conversation, index }))
    .sort((left, right) => {
      const leftPinned = Boolean(left.conversation.pinned)
      const rightPinned = Boolean(right.conversation.pinned)

      if (leftPinned !== rightPinned) {
        return leftPinned ? -1 : 1
      }

      if (leftPinned && rightPinned) {
        const pinnedDelta =
          getPinnedTimestamp(right.conversation) - getPinnedTimestamp(left.conversation)

        if (pinnedDelta !== 0) {
          return pinnedDelta
        }
      }

      return left.index - right.index
    })
    .map(({ conversation }) => conversation)
}

export function getConversationNextPageParam(lastPage: RuntimeConversationListDto) {
  if (!lastPage.has_more) {
    return undefined
  }

  return lastPage.next_last_id ?? lastPage.data.at(-1)?.id
}

export function replaceConversationInList(
  currentData: RuntimeConversationListDto | undefined,
  conversationId: string,
  updates: Partial<RuntimeConversationDto>,
) {
  if (!currentData) {
    return currentData
  }

  return {
    ...currentData,
    data: sortPinnedConversations(
      currentData.data.map((conversation) =>
        conversation.id === conversationId ? { ...conversation, ...updates } : conversation,
      ),
    ),
  }
}

export function removeConversationFromList(
  currentData: RuntimeConversationListDto | undefined,
  conversationId: string,
) {
  if (!currentData) {
    return currentData
  }

  return {
    ...currentData,
    data: currentData.data.filter((conversation) => conversation.id !== conversationId),
  }
}

/**
 * 首轮 streaming 可能先返回 conversation_id，再由列表接口稍后补全元数据。
 * 页面收到 id 后先把临时标题写入 cache，避免新会话在跳转后侧边栏短暂丢失。
 */
export function prependConversationIfMissing(
  currentData: RuntimeConversationListDto | undefined,
  conversation: RuntimeConversationDto,
) {
  const existingData = currentData ?? { data: [], has_more: false }
  const existingConversation = existingData.data.find((item) => item.id === conversation.id)

  if (existingConversation) {
    return existingData
  }

  return {
    ...existingData,
    data: sortPinnedConversations([conversation, ...existingData.data]),
  }
}

/**
 * 更新无限滚动历史缓存中的单条会话。
 *
 * rename/delete mutation 成功后需要同步普通列表和 Infinite Query 缓存；
 * 这里保持 pageParams 不变，只替换 pages，避免调用方误伤 TanStack Query 的分页游标。
 */
export function replaceConversationInPagedList(
  currentData: InfiniteData<RuntimeConversationListDto> | undefined,
  conversationId: string,
  updates: Partial<RuntimeConversationDto>,
) {
  if (!currentData) {
    return currentData
  }

  return {
    ...currentData,
    pages: currentData.pages.map(
      (page) => replaceConversationInList(page, conversationId, updates) ?? page,
    ),
  }
}

export function removeConversationFromPagedList(
  currentData: InfiniteData<RuntimeConversationListDto> | undefined,
  conversationId: string,
) {
  if (!currentData) {
    return currentData
  }

  return {
    ...currentData,
    pages: currentData.pages.map(
      (page) => removeConversationFromList(page, conversationId) ?? page,
    ),
  }
}
